import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  queryKeysSavedFilter,
  type QuerySavedFiltersResponse,
  SAVED_FILTER_NAME_MAX_LENGTH,
  upsertCreatedSavedFilterInCache,
  useMutationCreateSavedFilter,
  useMutationUpdateSavedFilter,
} from '~/data/savedFilter';

import ToastService from '~/services/toast.service';

import { hasHttpStatus } from '~/utils/httpError';

import { useUniversalFiltersSelector } from '../../hooks/useUniversalFiltersSelector';
import { UNIVERSAL_FILTER_ALL_ID } from '../../types';
import { useUniversalFiltersContext } from '../../UniversalFiltersProvider';

export function useFiltersDrawerSaveActions() {
  const { closeDrawer, config, savedFiltersState, store } =
    useUniversalFiltersContext();
  const queryClient = useQueryClient();
  const { t } = useTranslation('universalFilters');
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const draft = useUniversalFiltersSelector((context) => context.draft);
  const isDirty = useUniversalFiltersSelector((context) => context.isDirty);
  const selectedFilterId = useUniversalFiltersSelector(
    (context) => context.selectedFilterId,
  );

  const createSavedFilterMutation = useMutationCreateSavedFilter({
    onSuccess(savedFilter, variables) {
      queryClient.setQueryData<QuerySavedFiltersResponse>(
        queryKeysSavedFilter.getAll(variables.type),
        (previousCache) =>
          upsertCreatedSavedFilterInCache(previousCache, savedFilter),
      );
      store.trigger.saveSelectedFilterSucceeded({ savedFilter });
      ToastService.success([t(($) => $.drawer.notifications.saveSuccess)]);
      setIsCreateDialogOpen(false);
      closeDrawer();
    },
    onError(error) {
      ToastService.error([
        hasHttpStatus(error, 409)
          ? t(($) => $.drawer.notifications.createConflict)
          : t(($) => $.drawer.notifications.saveError),
      ]);
    },
  });
  const updateSavedFilterMutation = useMutationUpdateSavedFilter({
    onSuccess(savedFilter, variables) {
      store.trigger.saveSelectedFilterSucceeded({
        savedFilter: savedFilter ?? {
          filters: variables.filters ?? [],
          id: variables.savedFilterId,
        },
      });
      ToastService.success([t(($) => $.drawer.notifications.saveSuccess)]);
      closeDrawer();
    },
    onError(error) {
      ToastService.error([
        hasHttpStatus(error, 409)
          ? t(($) => $.drawer.notifications.updateConflict)
          : t(($) => $.drawer.notifications.saveError),
      ]);
    },
  });

  const isSavePending =
    createSavedFilterMutation.isPending || updateSavedFilterMutation.isPending;
  const hasSavedFiltersEnabled = Boolean(config.savedFilters?.enabled);
  const canSave = hasSavedFiltersEnabled && isDirty;
  const selectedFilterMeta = savedFiltersState.selectedFilterMeta;

  function handleSave() {
    if (!canSave || isSavePending) return;

    if (selectedFilterId === UNIVERSAL_FILTER_ALL_ID || !selectedFilterMeta) {
      setIsCreateDialogOpen(true);
      return;
    }

    updateSavedFilterMutation.mutate({
      expectedVersion: selectedFilterMeta.version,
      filters: [...draft],
      savedFilterId: selectedFilterMeta.id,
      type: config.type,
    });
  }

  function handleCreateSubmit(name: string) {
    const trimmedName = name.trim();
    if (!trimmedName || !canSave || isSavePending) return;

    if (trimmedName.length > SAVED_FILTER_NAME_MAX_LENGTH) {
      ToastService.error([t(($) => $.drawer.notifications.saveError)]);
      return;
    }

    createSavedFilterMutation.mutate({
      filters: [...draft],
      name: trimmedName,
      type: config.type,
    });
  }

  return {
    canSave,
    closeCreateDialog: () => {
      setIsCreateDialogOpen(false);
    },
    handleCreateSubmit,
    handleSave,
    hasSavedFiltersEnabled,
    isCreateDialogOpen,
    isDirty,
    isSavePending,
  };
}
